import { useSyncExternalStore } from 'react';

/**
 * Per-brand Menu Visibility, mirrored from the super-admin panel.
 *
 * The server resolves the brand from Host and returns these already merged over
 * the defaults on `GET /brand` (`menus`), so nothing here has to guess what a
 * missing id means. The ids MUST stay in step with the backend's
 * `brand-menus.ts` and the super-admin's `MenuVisibilityFields.tsx` — the three
 * lists are one vocabulary living in three repos.
 *
 * Hiding an entry removes the link; `menuRoutes.ts` then closes the URL behind
 * it, but only once EVERY entry linking to that page is off — see `MenuGate`.
 * The API behind the page still answers, so this is reachability, not access
 * control. Use Features to remove a product outright.
 */

/** Desktop left rail. `sec*` ids hide a whole section, header and children. */
export const SIDEBAR_MENU_ITEMS = [
  'referEarn',
  'secRacing', 'horse', 'greyhound',
  'secOthers', 'casino', 'vip', 'premium', 'virtual', 'live', 'slot', 'fantasy',
  'secAllSports',
  'politics', 'cricket', 'football', 'tennis', 'table-tennis', 'badminton',
  'esoccer', 'basketball', 'volleyball', 'snooker', 'ice-hockey', 'e-games',
  'futsal', 'handball', 'kabaddi', 'golf', 'rugby-league', 'boxing', 'mma',
  'motogp', 'american-football', 'soccer', 'wrestling',
] as const;

/** Main nav strip. Desktop draws every id; mobile only the ones it has. */
export const TOP_NAV_MENU_ITEMS = [
  'home', 'casino', 'slots', 'liveCasino', 'tableGames', 'crash', 'lottery',
  'inplay', 'cricket', 'tennis', 'football', 'tableTennis',
] as const;

/** Sport chips above the home-page odds table. */
export const SPORTS_TAB_MENU_ITEMS = [
  'cricket', 'soccer', 'tennis', 'table-tennis', 'esoccer', 'horse-racing',
  'greyhound-racing', 'basketball', 'wrestling', 'volleyball', 'badminton',
  'snooker', 'darts', 'boxing', 'mixed-martial-arts', 'american-football',
  'e-games', 'ice-hockey', 'futsal', 'politics', 'motor-sports', 'kabaddi',
] as const;

export type SidebarMenuItem = (typeof SIDEBAR_MENU_ITEMS)[number];
export type TopNavMenuItem = (typeof TOP_NAV_MENU_ITEMS)[number];
export type SportsTabMenuItem = (typeof SPORTS_TAB_MENU_ITEMS)[number];

export interface BrandMenus {
  sidebar: Record<SidebarMenuItem, boolean>;
  topNav: Record<TopNavMenuItem, boolean>;
  sportsTabs: Record<SportsTabMenuItem, boolean>;
}

const MENU_ITEMS: { [G in keyof BrandMenus]: readonly string[] } = {
  sidebar: SIDEBAR_MENU_ITEMS,
  topNav: TOP_NAV_MENU_ITEMS,
  sportsTabs: SPORTS_TAB_MENU_ITEMS,
};

function allOn<T extends readonly string[]>(ids: T): Record<T[number], boolean> {
  const out = {} as Record<T[number], boolean>;
  ids.forEach((id) => { out[id as T[number]] = true; });
  return out;
}

/** Resting state — matches the backend's DEFAULT_BRAND_MENUS exactly. */
export const DEFAULT_BRAND_MENUS: BrandMenus = {
  sidebar: allOn(SIDEBAR_MENU_ITEMS),
  topNav: allOn(TOP_NAV_MENU_ITEMS),
  sportsTabs: allOn(SPORTS_TAB_MENU_ITEMS),
};

/* --- store ---------------------------------------------------------------- */
// Same shape as the Site Settings store next door: useSyncExternalStore compares
// snapshots by identity, so the object is rebuilt only when something changes.
let snapshot: BrandMenus = DEFAULT_BRAND_MENUS;
const subs = new Set<() => void>();

function subscribe(cb: () => void) {
  subs.add(cb);
  return () => { subs.delete(cb); };
}

/** Merge whatever the server sent over the defaults; ignore junk ids. */
export function setBrandMenus(raw: unknown): void {
  const next: BrandMenus = {
    sidebar: { ...DEFAULT_BRAND_MENUS.sidebar },
    topNav: { ...DEFAULT_BRAND_MENUS.topNav },
    sportsTabs: { ...DEFAULT_BRAND_MENUS.sportsTabs },
  };
  if (raw && typeof raw === 'object') {
    const src = raw as Record<string, unknown>;
    (Object.keys(next) as (keyof BrandMenus)[]).forEach((group) => {
      const stored = src[group];
      if (!stored || typeof stored !== 'object') return;
      const groupSrc = stored as Record<string, unknown>;
      const target = next[group] as Record<string, boolean>;
      MENU_ITEMS[group].forEach((id) => {
        if (typeof groupSrc[id] === 'boolean') target[id] = groupSrc[id] as boolean;
      });
    });
  }
  snapshot = next;
  subs.forEach((f) => f());
}

/* --- hooks ---------------------------------------------------------------- */
export function useBrandMenus(): BrandMenus {
  return useSyncExternalStore(subscribe, () => snapshot);
}

/** Non-reactive read, for code outside the React tree. */
export function getBrandMenus(): BrandMenus {
  return snapshot;
}

/**
 * Filter a list by its menu ids, against a map you already hold.
 *
 * Takes the map rather than reading the store so it stays honest inside render:
 * pass what `useBrandMenus()` returned and the result re-computes when the brand
 * resolves. `getId` returning undefined means the entry predates the vocabulary
 * and is always kept — hiding something an operator has no toggle for would be a
 * bug they could not undo from the panel.
 */
export function filterByMenu<T>(
  items: readonly T[],
  map: Record<string, boolean>,
  getId: (item: T) => string | undefined,
): T[] {
  return items.filter((item) => {
    const id = getId(item);
    return id === undefined || map[id] !== false;
  });
}
